"use client";

import React, { useEffect, useRef, useState } from "react";
import { Ruler } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
export default function ArchitecturalPrecision() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.1
  });
  return <section data-section-id="225622" id="founderprofile" ref={ref} className={`bg-secondary text-secondary-foreground py-16 px-6 md:px-12 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8">
        {}
        <div className="flex flex-col justify-center">
          <h2 className="font-default text-4xl md:text-5xl lg:text-6xl font-bold text-primary leading-tight tracking-tighter mb-6 uppercase">
            FOUNDER & DIRECTOR
          </h2>
          <p className="text-lg md:text-xl text-secondary-foreground/80 leading-relaxed mb-8 max-w-xl font-default">
            Jonathon Gilliver leads Bottom Up Projects with over 15 years of construction expertise. His vision for high-quality commercial fit-outs and remedial works is built on a foundation of transparency, safety, and technical excellence across the Sydney region.
          </p>
          <p className="text-lg md:text-xl text-secondary-foreground/80 leading-relaxed mb-8 max-w-xl font-default">
            Our expertise extends into the industrial construction sector, where we deliver robust solutions for warehouses and large-scale facilities. We understand the unique structural and operational requirements of industrial environments, ensuring every project meets rigorous safety and durability standards.
          </p>

          <div className="mb-10 max-w-xl border-l border-primary/30 pl-6 py-1">
            <h4 className="font-mono text-xs font-bold text-primary mb-3 tracking-widest uppercase">
              Operational Methodology
            </h4>
            <p className="text-base text-secondary-foreground/70 leading-relaxed font-default">
              We combine industry-leading project management software with transparent communication to deliver efficient and accountable project outcomes. Our approach prioritises clear reporting, streamlined processes, and client involvement from planning through to completion.
            </p>
          </div>

          <div className="mb-10">
            <Link to="https://set-starfish.10web.cloud/wp-content/uploads/2026/05/Mark-Cormack-Vaughan-Constructions-17-Oct-2025.pdf" newTab className="inline-flex items-center justify-center px-8 py-4 bg-primary text-primary-foreground font-mono text-sm font-bold tracking-widest uppercase hover:bg-primary/90 transition-colors">
              Director Reference
            </Link>
          </div>
          
          <div className="grid grid-cols-2 gap-8 mt-auto">
            <div className="overflow-hidden border border-primary/20">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1641543/img/industrial_protection_specifications_layout.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1641543/img/industrial_protection_specifications_layout.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1641543/img/industrial_protection_specifications_layout.png-wvc-sizes" alt="Construction Safety Excellence Badge" loading="lazy" className="w-full h-full object-cover aspect-[4/3] hover:scale-105 transition-transform duration-700" />
            </div>
            <div className="overflow-hidden border border-primary/20">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1641543/img/carpark_asset_warehouse_protection_blueprint.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1641543/img/carpark_asset_warehouse_protection_blueprint.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1641543/img/carpark_asset_warehouse_protection_blueprint.png-wvc-sizes" alt="Quality Assurance and Compliance Badge" loading="lazy" className="w-full h-full object-cover aspect-[4/3] hover:scale-105 transition-transform duration-700" />
            </div>
          </div>
        </div>

        {}
        <div className="flex flex-col gap-8">
          <div className="overflow-hidden flex-grow border border-primary/20">
            <img src="https://bottomupprojects.com.au/wp-content/uploads/2026/05/ChatGPT-Image-May-12-2026-05_28_44-PM.png" alt="Jonathon Gilliver - Founder & Director of Bottom Up Projects" loading="lazy" className="w-full h-full object-cover aspect-[3/4] lg:aspect-auto" />
          </div>
          
          <Card className="border-primary/30 shadow-lg bg-secondary/50 backdrop-blur-md">
            <CardContent className="p-6 md:p-8 flex items-start gap-6">
              <div className="shrink-0 mt-1">
                <Ruler strokeWidth={1} className="w-10 h-10 text-primary" />
              </div>
              <div>
                <h3 className="font-mono text-xl font-bold text-primary mb-3 tracking-widest uppercase">
                  LEADERSHIP & VALUES
                </h3>
                <p className="text-secondary-foreground/70 leading-relaxed font-default">
                  Safety, quality, and sustainability are at the heart of every project. We deliver compliant, reliable solutions through structured processes and a commitment to operational excellence in every refurbishment and maintenance task.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>;
}